import { useForm } from 'react-hook-form';
import {
  ChangeEvent,
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  attachFiles,
  catchErrorMessage,
  getExactFileName,
  getFileDetails,
  handlePageScrollPosition,
} from 'utility/helpers';
import KnowledgeAPI, { FeedFilter } from 'api/KnowledgeAPI';
import { toast } from 'react-toastify';
import { DRAFTS, HOME, VIEW_DRAFT } from 'constants/routes';
import useUserData, { useUserArticlesQuery } from 'hooks/useUserData';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  ArticleSourceType,
  ArticleType,
  AttachmentType,
  CustomFieldValueType,
  useArticlesAndPlaylistsPaginationQuery,
} from 'pages/knowledge/hooks/useArticlesQuery';
import useArticleDetailsQuery, {
  updateArticleCommentsCache,
  updateArticleDetailsCache,
} from 'pages/article/hooks/useArticleDetailsQuery';
import {
  ProjectsPaginationResponseType,
  ProjectRecommendationsType,
} from 'pages/projects/hooks/useProjectsQuery';
import { updateDraftsCache } from 'pages/drafts/hooks/useDraftsQuery';
import axios, { AxiosResponse } from 'axios';
import _ from 'lodash';
import { AUDIO_TYPES, DOCUMENT_TYPES, IMAGE_TYPES, VIDEO_TYPES } from 'components/utils/constants';
import { updateProjectRecommendationsQueryCache } from 'pages/project/hooks/useProjectDetailsQuery';
import { updateStakeholderArticlesCache } from 'pages/stakeholder/hooks/useStakeholderDetailsQuery';
import { useSearchItemQuery } from 'hooks/useSearchQuery';
import useBookmarksQuery from 'pages/bookmarks/hooks/useBookmarksQuery';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { ConfigEntryType, ConfigFieldEntryType } from 'config/config';
import { TemplateType } from '@buildwise/libs/company-admin';
import CompanyAPI from 'api/CompanyAPI';
import {
  CONFIGS,
  getCustomFieldsDefaultValues,
  getCustomFieldsValidationSchema,
  useConfigQuery,
} from '@buildwise/libs/configurations';
import { useDropzone } from 'react-dropzone';
import {
  getPredefinedFieldDefaultValue,
  getPredefinedFieldValidation,
  useArticleInputFields,
} from '@buildwise/libs/posts';

export type PostFormData = {
  isDraft: boolean;
  isWritingWithAI: boolean;
  [key: string]: any;
};

type UseAddPostFormOptions = {
  articleType: ArticleSourceType;
  articleId?: string;
  articleFormState?: PostFormData;
  createPlaylist?: boolean;
  data?: ArticleType;
  defaultPlaylists?: { label: string; value: string }[];
  defaultProjectId?: string;
  formIds?: string[];
  forward?: boolean;
  selectedFeedFilters?: FeedFilter[];
  isCopyPost?: boolean;
  isEditing?: boolean;
  project?: ProjectRecommendationsType;
  publishArticleToCompanyIds?: string[];
  stakeholderId?: string;
  onAddArticleCallback?: (options: { article: ArticleType }) => void;
  onSaveCallback?: (article: ArticleType) => void;
  setOpen: Dispatch<SetStateAction<boolean>>;
  updateArticleCallback?: (options: {
    oldArticle: ArticleType;
    updatedArticle: ArticleType;
  }) => void;
  state?: any;
};

type ProjectsSearchResponseType = ProjectsPaginationResponseType;

const createPostFormSchema = ({
  articleFields,
  isCreatingOrEditingPlaylist,
}: {
  articleFields: ConfigEntryType[];
  isCreatingOrEditingPlaylist: boolean;
}) => {
  const customFieldsValidations = getCustomFieldsValidationSchema({
    fields: articleFields,
    doNotAddRequiredValidation: isCreatingOrEditingPlaylist,
    getPredefinedFieldValidation: ({ field }) =>
      getPredefinedFieldValidation({ field, isCreatingOrEditingPlaylist }),
  });

  return Yup.object().shape({
    ...customFieldsValidations,
    // Check if divisions is enabled, then provide respective divisionChips validation schema
    ...(customFieldsValidations?.['divisions'] && {
      divisionChips: Yup.array(Yup.string()),
    }),
    // Check if specSections is enabled, then provide respective specSectionChips validation schema
    ...(customFieldsValidations?.['specSections'] && {
      specSectionChips: Yup.array(Yup.string()),
    }),
    isDraft: Yup.boolean(),
    isWritingWithAI: Yup.boolean(),
  });
};

const useAddPostForm = ({
  articleFormState,
  articleId,
  articleType,
  createPlaylist = false,
  data: articleData,
  defaultPlaylists = [],
  defaultProjectId = '',
  formIds = [],
  forward,
  selectedFeedFilters,
  isCopyPost,
  isEditing = false,
  project,
  publishArticleToCompanyIds = [],
  stakeholderId,
  onAddArticleCallback,
  onSaveCallback,
  setOpen,
  updateArticleCallback,
}: UseAddPostFormOptions) => {
  const { user, companyId, isNovaAdmin, company, isBuildWiseInsightsUser } = useUserData();
  const { updateFeedCache, resetFeedQueries } = useArticlesAndPlaylistsPaginationQuery({
    shouldFetch: false,
    filters: selectedFeedFilters,
  });
  const { updateUserArticlesCache } = useUserArticlesQuery({ shouldFetch: false });
  const { updateBookmarksCache } = useBookmarksQuery({ shouldFetch: false });
  const { data: projectsData } = useSearchItemQuery<ProjectsSearchResponseType>(
    {
      searchType: 'projects',
      operator: 'or',
      filterBy: {
        filters: [
          {
            filterType: 'name',
            filterValue: 'ALL',
          },
        ],
      },
    },
    false,
  );
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [isSavingDraft, setSavingDraft] = useState(false);
  const [isFullScreen, setFullScreen] = useState(false);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [openConfirmation, setOpenConfirmation] = useState(false);
  const [startAnotherPost, setStartAnotherPost] = useState<boolean>(false)
  const canPublishPost = useMemo(
    () => !((isNovaAdmin || isBuildWiseInsightsUser) && company?.settings?.externalFlag),
    [company?.settings.externalFlag, isNovaAdmin, isBuildWiseInsightsUser],
  );
  const [discardLoading, setDiscardLoading] = useState<boolean>(false);
  const { articleResponse: fetchedArticle } = useArticleDetailsQuery({
    articleId: articleId || articleData?.id || '',
    updateViewCount: false,
  });
  const data = useMemo(
    () => fetchedArticle?.companyArticle?.article || articleData,
    [articleData, fetchedArticle],
  );
  const { config: hashtagConfig, updateConfigCache } = useConfigQuery({
    companyId,
    configName: CONFIGS.HASHTAGS.configName,
  });
  const isCreatingOrEditingPlaylist = useMemo(
    () => createPlaylist || data?.originIndicator === 'PLAYLIST',
    [createPlaylist, data],
  );
  const { articleFormConfigName, articleFields, isLoadingArticleConfigFields } =
    useArticleInputFields({
      articleType,
      isEditing,
    });
  const postForm = useForm<PostFormData>({
    resolver: yupResolver(
      createPostFormSchema({
        articleFields,
        isCreatingOrEditingPlaylist,
      }),
    ),
    defaultValues: getDefaultValues(),
  });
  const [selectedTemplate, setSelectedTemplate] = useState<TemplateType | undefined>(undefined);
  const [openTemplates, setOpenTemplates] = useState(false);
  const {
    formState: { isDirty, dirtyFields },
    getValues,
    handleSubmit,
    reset,
    setValue,
    resetField
  } = postForm;
  const onDrop = useCallback((acceptedFiles: File[]) => {
    setLoadingFiles(true);
    attachFiles(acceptedFiles, getValues('attachments') || [], (attachments: AttachmentType[]) => {
      setValue('attachments', attachments, { shouldDirty: true, shouldValidate: true });
      setLoadingFiles(false);
    });
  }, []);
  const { getRootProps, isDragActive } = useDropzone({ onDrop });

  useEffect(() => {
    const { title } = getValues();
    if (title && isCopyPost) {
      setValue('title', `Copy of - ${title}`, { shouldDirty: true, shouldValidate: true });
    }
  }, [isCopyPost, getValues, setValue]);

  useEffect(() => {
    if (forward) {
      downloadArticleAttachments();
    }
  }, [forward]);

  useEffect(() => {
    if (fetchedArticle?.companyArticle?.article) {
      reset(getDefaultValues(), {
        keepDirtyValues: true,
        keepErrors: true,
        keepTouched: true,
      });
    }
  }, [fetchedArticle?.companyArticle?.article]);

  useEffect(() => {
    if (articleFormState) {
      reset(articleFormState, {
        keepDirtyValues: true,
        keepErrors: true,
        keepTouched: true,
      });
    }
  }, [articleFormState]);

  function getDefaultValues() {
    const fieldsDefaultValues = getCustomFieldsDefaultValues({
      data,
      fields: articleFields,
      getPredefinedFieldDefaultValue: ({ field }) =>
        getPredefinedFieldDefaultValue({
          article: data,
          defaultPlaylists,
          defaultProjectId,
          field,
        }),
    });
    return {
      ...fieldsDefaultValues,
      // Check if divisions is enabled, then provide respective divisionChips default values
      ...(fieldsDefaultValues?.['divisions'] && {
        divisionChips: [],
      }),
      // Check if specSections is enabled, then provide respective specSectionChips default values
      ...(fieldsDefaultValues?.['specSections'] && {
        specSectionChips: [],
      }),
      isDraft: !!data?.isDraft,
      playlists: defaultPlaylists || [],
      isWritingWithAI: false,
    };
  }

  const downloadArticleAttachments = useCallback(async () => {
    const downloadAttachmentsPromises: Promise<boolean>[] = [];
    const allFiles: File[] = [];

    if (data?.attachments) {
      setLoadingFiles(true);
      const attachmentsToClone = data?.attachments.filter(
        (attachment) => !attachment.imageHashValue,
      );

      for (const attachment of attachmentsToClone) {
        downloadAttachmentsPromises.push(
          new Promise(async (resolve, reject) => {
            try {
              const { data: fileBlob } = await axios(attachment.articleOpenUrl || '', {
                responseType: 'blob',
              });

              let fileType = '';

              if (IMAGE_TYPES.includes(attachment.fileType?.toLowerCase())) {
                fileType = `image/${attachment.fileType?.toLowerCase()}`;
              }

              if (VIDEO_TYPES.includes(attachment.fileType?.toLowerCase())) {
                fileType = `video/${attachment.fileType?.toLowerCase()}`;
              }

              if (AUDIO_TYPES.includes(attachment.fileType?.toLowerCase())) {
                fileType = `audio/${attachment.fileType?.toLowerCase()}`;
              }

              if (DOCUMENT_TYPES.includes(attachment.fileType?.toLowerCase())) {
                if (attachment.fileType?.toLowerCase() === 'txt') {
                  fileType = `text/${attachment.fileType?.toLowerCase()}`;
                } else {
                  fileType = `application/${attachment.fileType?.toLowerCase()}`;
                }
              }

              const newFile = new File([fileBlob], getExactFileName(attachment), {
                lastModified: new Date().getTime(),
                type: fileType,
              });
              allFiles.push(newFile);
              resolve(true);
            } catch (error) {
              reject(error);
            }
          }),
        );
      }

      try {
        await Promise.all(downloadAttachmentsPromises);
        attachFiles(allFiles, getValues('attachments') || [], (attachments: AttachmentType[]) => {
          setValue('attachments', attachments, { shouldDirty: true, shouldValidate: true });
        });
      } catch (error) {
        console.error('DOWNLOAD_ARTICLE_ATTACHMENTS_ERROR', error);
      } finally {
        setLoadingFiles(false);
      }
    }
  }, [data]);

  const closeModal = useCallback(
    async (article?: ArticleType) => {
      try {
        if (
          !!article &&
          ['CONVERT_FROM_FILE', 'CONVERT_FROM_TEXT'].includes(article?.originIndicator) &&
          article?.isDraft
        ) {
          setDiscardLoading(true);
          const payload: any = {
            ...article,
            isDraft: false,
            status: 'DISCARDED',
          };
          const articleResponse = await KnowledgeAPI.editArticle(companyId, article.id, payload);
          if (articleResponse?.data?.id) {
            toast.success('Post discarded successfully!');
          }
        }
      } catch (error) {
        toast.error(catchErrorMessage(error));
      } finally {
        setDiscardLoading(false);
        setSelectedTemplate(undefined);
        setOpen(false);
        navigate(pathname);
      }
    },
    [companyId, pathname],
  );

  const addSuggestionComment = async (comment: string) => {
    try {
      if (user && data?.id && comment.trim()) {
        const { data: commentResData } = await KnowledgeAPI.addComment(companyId, data.id, {
          commentAuthor: user?.username,
          commentText: comment?.trim(),
          mentions: [],
        });
        updateArticleCommentsCache(companyId, data.id, commentResData?.comment, 'add');
      }
    } catch (error) {
      console.log('ADD_SUGGESTION_COMMENT_ERROR', error);
    }
  };

  const handleSave = useCallback(
    async (values: PostFormData) => {
      if (values.isDraft) {
        setSavingDraft(true);
      }

      try {
        const fetchedHashtags = hashtagConfig?.entries.map((tag) => tag.entryCode) || [];
        const payload: Record<string, any> = {
          isDraft: values.isDraft,
          status: values.isDraft ? data?.status : 'PUBLISHED',
          articleType,
        };

        if (formIds.length > 0) {
          payload['formIds'] = formIds;
        }

        // Format config field values
        const customFieldsValues: Record<string, CustomFieldValueType> = {};
        const articleKeys = Object.keys(data || {});
        articleFields.forEach((field) => {
          if (
            Array.isArray(values[field.entryCode])
              ? values[field.entryCode].length > 0
              : values[field.entryCode]
          ) {
            if (
              (articleKeys.includes(field.entryCode) ||
                field.entryType?.toUpperCase() !== ConfigFieldEntryType.CUSTOM) &&
              field.entryCode !== 'attachments'
            ) {
              payload[field.entryCode] = values[field.entryCode];
            } else {
              if (field.entryType?.toUpperCase() === ConfigFieldEntryType.CUSTOM) {
                customFieldsValues[field.entryCode] = {
                  fieldName: field.entryCode,
                  fieldValue: values[field.entryCode],
                  type: field.referenceObject?.type || '',
                  configRefName: field.referenceObject?.configRefName || '',
                  label: field.referenceObject?.label || field.entryDescription,
                };
              }
            }
          }
        });
        payload['customFields'] = customFieldsValues;

        // Extract mentions from description
        const parser = new DOMParser();
        const doc = parser.parseFromString(payload?.description, 'text/html');
        const mentionElements = doc.querySelectorAll('.mention');
        const mentions = mentionElements?.length
          ? Array.from(mentionElements).map((el) => {
              const name = el?.textContent || '';
              return {
                userName: el?.getAttribute('data-mention-id'),
                displayName: name,
              };
            })
          : [];
        payload['mentions'] = mentions;

        // Extract hashtags from description
        let descHashtags: string[] = (payload?.description?.match(/#\w+/g) || []).map((tag) =>
          tag.slice(1),
        );

        let newHashTags: string[] = [];
        if (!!descHashtags?.length) {
          newHashTags.push(..._.difference(descHashtags, fetchedHashtags));
        }

        // Add new hashtags to company config if any
        if (newHashTags.length) {
          const entries = hashtagConfig?.entries || [];
          const updatedEntries: ConfigEntryType[] = [
            ...entries,
            ...newHashTags?.map((topic) => ({
              entryCode: topic,
              entryDescription: topic,
              isActive: true,
              entryValues: [],
            })),
          ];
          const { data } = await CompanyAPI.updateConfig(
            companyId,
            CONFIGS.HASHTAGS.configName,
            hashtagConfig?.description || 'Company hashtags',
            updatedEntries,
          );
          updateConfigCache({
            companyId,
            configName: CONFIGS.HASHTAGS.configName,
            updatedConfig: data,
          });
        }
        payload['labels'] = descHashtags;

        if (values.playlists || values.playListArticleIds) {
          payload.playListArticleIds = _.uniq([
            ...(values?.playListArticleIds || []),
            ...(values?.playlists?.map?.((playlist) => playlist.value) || []),
          ]);
        }

        if (values.divisions || values.divisionChips) {
          payload.divisions = _.uniq([
            ...(values.divisionChips || []),
            ...(values.divisions || []),
          ]);
        }
        if (values.specSections || values.specSectionChips) {
          payload.specSections = _.uniq([
            ...(values.specSectionChips || []),
            ...(values.specSections || []),
          ]);
        }

        // Write with AI Origin indicator
        delete payload.isWritingWithAI;
        if (values.isWritingWithAI) {
          payload.originIndicator = 'CONVERT_FROM_TEXT';
        }

        let coverPhoto: AttachmentType | undefined = undefined;
        let remainingAttachments: AttachmentType[] = [...(values.attachments || [])];

        if (!isEditing) {
          const coverPhotoIndex = remainingAttachments.findIndex(
            (attachment) => getFileDetails({ file: attachment }).isMediaFile,
          );

          if (coverPhotoIndex > -1) {
            coverPhoto = remainingAttachments[coverPhotoIndex];
            remainingAttachments.splice(coverPhotoIndex, 1);
          }
        }

        let articleResponse;
        if (isEditing && data?.id) {
          // payload.playListArticleIds =
          //   values?.playListArticleIds?.length > 0 ? values.playListArticleIds : [];

          // Suggestion fields
          delete payload.comment;
          addSuggestionComment(values.comment || '');

          const updatedPayload = {
            ...data,
            ...payload,
          };
          delete updatedPayload?.project;
          delete updatedPayload?.primaryCompanyName;

          articleResponse = await KnowledgeAPI.editArticle(companyId, data.id, updatedPayload);
        } else {
          delete payload.attachments;
          if (createPlaylist) {
            payload.originIndicator = 'PLAYLIST';
          }

          if (isCopyPost) {
            payload.attachments = remainingAttachments.filter(
              (file) => file.fileUploaded && !file.isRemoved && !file.isUpdated,
            );
          }

          if (coverPhoto) {
            payload.attachment = coverPhoto;
          }

          // Suggestion fields
          if (articleType === ArticleSourceType.SUGGESTION) {
            payload['taskStatus'] = 'NEW';
          }

          articleResponse = await KnowledgeAPI.addArticle(companyId, payload);
        }

        let removableFiles =
          remainingAttachments
            ?.filter((file) => file.fileUploaded && file.isRemoved)
            .map((file) => file.id) || [];
        let newFiles = remainingAttachments?.filter((file) => !file.fileUploaded) || [];
        let updatedFiles =
          remainingAttachments?.filter((file) => file.fileUploaded && file.isUpdated) || [];

        if (isCopyPost) {
          removableFiles = [];
          updatedFiles = [];
          newFiles =
            remainingAttachments?.filter((file) => !file.fileUploaded || file.isUpdated) || [];
        }

        if (newFiles.length || updatedFiles.length || removableFiles.length) {
          const updateArticleMediaPromises: Promise<AxiosResponse<any, Error> | any>[] = [];

          if (newFiles.length) {
            const formData = new FormData();

            newFiles.forEach((attachment) => {
              formData.append('file', attachment.file);
              if (attachment.thumbnail) {
                formData.append('thumbnail', attachment.thumbnail);
              }
            });

            updateArticleMediaPromises.push(
              KnowledgeAPI.addArticleMedia(companyId, articleResponse?.data?.id, formData),
            );
          }

          if (updatedFiles.length) {
            const formData = new FormData();

            updatedFiles.forEach((attachment) => {
              formData.append('file', attachment.file);
              if (attachment.thumbnail) {
                formData.append('thumbnail', attachment.thumbnail);
              }
            });

            updateArticleMediaPromises.push(
              KnowledgeAPI.updateArticleMedia(companyId, articleResponse?.data?.id, formData),
            );
          }

          if (removableFiles.length) {
            const removeAttachmentsPromises = removableFiles.map((id) =>
              KnowledgeAPI.removeAttachment(companyId, articleResponse?.data?.id, id),
            );

            updateArticleMediaPromises.push(Promise.all(removeAttachmentsPromises));
          }

          const [res1, res2] = await Promise.all(updateArticleMediaPromises);

          let updatedArticle: ArticleType | undefined = articleResponse?.data;

          if ((newFiles.length || updatedFiles.length) && res1) {
            updatedArticle = res1.data;
          }

          if (newFiles.length && updatedFiles.length && res2) {
            updatedArticle = res2.data;
          }

          if (!_.isEmpty(updatedArticle)) {
            if (removableFiles.length) {
              updatedArticle = {
                ...updatedArticle,
                attachments:
                  updatedArticle?.attachments?.filter(
                    (attachment) => !removableFiles.includes(attachment.id),
                  ) || [],
              };
            }

            if (data && !updatedArticle?.isDraft) {
              updateArticleCallback?.({
                oldArticle: data,
                updatedArticle: {
                  ...updatedArticle,
                  project:
                    (updatedArticle.projectId || data?.projectId) && projectsData?.projects
                      ? projectsData?.projects?.find(
                          (project) => project.id === updatedArticle?.projectId,
                        )
                      : undefined,
                },
              });
            }

            if (onSaveCallback) {
              onSaveCallback(updatedArticle);
            } else {
              if (updatedArticle && !updatedArticle.isDraft) {
                updateFeedCache({
                  action: 'update',
                  article: updatedArticle,
                });
                if (updatedArticle.isArticleBookMarked) {
                  updateBookmarksCache({ action: 'update', article: updatedArticle });
                }
                if (project?.id) {
                  updateProjectRecommendationsQueryCache({
                    companyId,
                    projectId: project?.id,
                    articles: [updatedArticle],
                    action: 'update-article',
                  });
                }
                // TODO: update cache of GET /playlists API
              }

              if (updatedArticle && updatedArticle.isDraft) {
                updateDraftsCache(companyId, updatedArticle, 'update');
              }

              if (isEditing && updatedArticle) {
                updateArticleDetailsCache(companyId, {
                  ...updatedArticle,
                  project:
                    (updatedArticle.projectId || data?.projectId) && projectsData?.projects
                      ? projectsData?.projects?.find(
                          (project) => project.id === updatedArticle?.projectId,
                        )
                      : undefined,
                });
              }
            }
          }
        } else {
          if (data && !articleResponse?.data?.isDraft) {
            updateArticleCallback?.({
              oldArticle: (data || fetchedArticle?.companyArticle?.article) as ArticleType,
              updatedArticle: {
                ...articleResponse?.data,
                project:
                  (articleResponse?.data.projectId || data?.projectId) && projectsData?.projects
                    ? projectsData?.projects?.find(
                        (project) => project.id === articleResponse?.data?.projectId,
                      )
                    : undefined,
              },
            });
          }

          onAddArticleCallback?.({
            article: articleResponse?.data,
          });
          if (onSaveCallback && !articleResponse?.data?.isDraft) {
            onSaveCallback({
              ...articleResponse?.data,
              playListArticleIds: payload?.playListArticleIds || [],
            });
          } else {
            if (!articleResponse?.data?.isDraft) {
              updateFeedCache({
                action: 'update',
                article: articleResponse.data,
              });
              updateUserArticlesCache({
                action: 'update',
                article: articleResponse?.data,
                _username: articleResponse?.data?.createdBy,
              });
              if (articleResponse?.data?.isArticleBookMarked) {
                updateBookmarksCache({ action: 'update', article: articleResponse?.data });
              }
              if (project?.id) {
                updateProjectRecommendationsQueryCache({
                  companyId,
                  projectId: project?.id,
                  articles: [articleResponse.data],
                  action: 'update-article',
                });
              }
            }

            if (isEditing) {
              updateArticleDetailsCache(companyId, {
                ...articleResponse.data,
                attachments: data?.attachments?.filter(
                  (file) => !removableFiles?.includes(file.id),
                ),
                project:
                  (articleResponse.data.projectId || data?.projectId) && projectsData?.projects
                    ? projectsData?.projects?.find(
                        (project) => project.id === articleResponse?.data.projectId,
                      )
                    : undefined,
              });
            }
          }
        }

        if (payload.originIndicator === 'PLAYLIST' || data?.originIndicator === 'PLAYLIST') {
          toast.success(
            values.isDraft
              ? 'Collection saved to drafts'
              : isEditing && !data?.isDraft
                ? 'Collection has been updated'
                : 'Collection created',
          );
        } else {
          toast.success(
            values.isDraft
              ? 'Draft saved successfully'
              : isEditing && !data?.isDraft
                ? 'Post updated successfully'
                : 'Post Published',
          );

          if (!isEditing && !articleResponse?.data?.isDraft) {
            handlePageScrollPosition('posts', 0, 0, 'reset');
            handlePageScrollPosition('collections', 0, 0, 'reset');
            resetFeedQueries();
          }
        }
        if (data?.isDraft && !articleResponse.data.isDraft && !isCopyPost) {
          updateDraftsCache(companyId, articleResponse.data, 'delete');
          navigate(HOME);
        }

        // TODO: Move this cache updation to respective page
        if (
          stakeholderId &&
          values.partnerIds &&
          data?.partnerIds?.length !== values?.partnerIds?.length
        ) {
          updateStakeholderArticlesCache('delete', stakeholderId, articleResponse.data);
        }
        if(!startAnotherPost){
          closeModal();
        } else{
          const fieldsToReset = ['description', 'title', 'attachments', 'divisions', 'specSections','specSectionChips','divisionChips'];
          fieldsToReset.forEach(field => resetField(field));
          setValue('divisionChips', []);
          setValue('specSectionChips', [])
          setStartAnotherPost(false)
        }
        

        if (values.isDraft) {
          if (!(newFiles.length || updatedFiles.length || removableFiles.length)) {
            updateDraftsCache(companyId, articleResponse.data, 'update');
          }

          if (pathname !== VIEW_DRAFT && !onSaveCallback && !startAnotherPost) {
            navigate(DRAFTS);
          }
        }
      } catch (error) {
        toast.error(catchErrorMessage(error));
      } finally {
        setSavingDraft(false);
      }
    },
    [
      hashtagConfig,
      formIds,
      isEditing,
      data,
      companyId,
      isCopyPost,
      createPlaylist,
      projectsData,
      project,
      stakeholderId,
      pathname,
      articleFields,
    ],
  );

  const onFileInputChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const files = [...((event?.target?.files || []) as File[])];
    attachFiles(files, getValues('attachments') || [], (attachments: AttachmentType[]) => {
      setValue('attachments', attachments, { shouldDirty: true, shouldValidate: true });
    });
  }, []);

  const handleTemplateSelection = useCallback(
    (template: TemplateType) => {
      if (template.id !== selectedTemplate?.id) {
        setSelectedTemplate(template);
        setValue('title', template.title, {
          shouldDirty: true,
          shouldValidate: true,
          shouldTouch: true,
        });
        setValue('labels', template.labels, {
          shouldDirty: true,
          shouldValidate: true,
          shouldTouch: true,
        });
        setValue('description', template.description, {
          shouldDirty: true,
          shouldValidate: true,
          shouldTouch: true,
        });
      }

      setOpenTemplates(false);
    },
    [selectedTemplate],
  );

  const handleClose = useCallback(() => {
    if (isDirty && Object.keys(dirtyFields).length && !forward) {
      setOpenConfirmation(true);
    } else {
      closeModal();
    }
  }, [isDirty, dirtyFields, forward, closeModal]);

  const onSaveDraft = useCallback(
    handleSubmit(
      (values) => handleSave({ ...values, isDraft: true }),
      () => {
        // Close action modal
        setOpenConfirmation(false);
      },
    ),
    [handleSave],
  );

  const onSubmit = useCallback(
    handleSubmit((values) => handleSave({ ...values, isDraft: false })),
    [handleSave],
  );

  const onForward = useCallback(
    handleSubmit(async (values: PostFormData) => {
      try {
        const payload: Record<string, string | number | string[]> = {
          title: values.title?.trim(),
          labels: values.labels,
          description: values.description,
          publishArticleToCompanyIds,
          forwardedFromArticleId: data?.id || '',
        };

        if (values.divisions || values.divisionChips) {
          payload.divisions = _.uniq([
            ...(values.divisionChips || []),
            ...(values.divisions || []),
          ]);
        }
        if (values.specSections || values.specSectionChips) {
          payload.specSections = _.uniq([
            ...(values.specSectionChips || []),
            ...(values.specSections || []),
          ]);
        }

        const formData = new FormData();
        formData.append('article', JSON.stringify(payload));

        values.attachments?.forEach?.((attachment) => {
          formData.append('file', attachment.file);
          if (attachment.thumbnail) {
            formData.append('thumbnail', attachment.thumbnail);
          }
        });

        await KnowledgeAPI.forwardArticle(companyId, formData);
        toast.success('Post forwarded successfully!');
        closeModal();
      } catch (error) {
        toast.error(catchErrorMessage(error));
      }
    }),
    [data, publishArticleToCompanyIds, companyId],
  );

  return {
    articleFormConfigName,
    article: data,
    articleFields,
    canPublishPost,
    companyId,
    discardLoading,
    isCreatingOrEditingPlaylist,
    isLoadingArticleConfigFields,
    isDragActive,
    isFullScreen,
    isNovaAdmin,
    isSavingDraft,
    loadingFiles,
    openConfirmation,
    openTemplates,
    postForm,
    selectedTemplate,
    startAnotherPost,
    closeModal,
    getRootProps,
    handleClose,
    handleTemplateSelection,
    onDrop,
    onFileInputChange,
    onForward,
    onSaveDraft,
    onSubmit,
    setFullScreen,
    setOpenConfirmation,
    setOpenTemplates,
    setStartAnotherPost
  };
};

export default useAddPostForm;
